import {
  Login as LoginIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useLocation, useNavigate } from "react-router-dom";
import authService from "../../services/auth";
import EsqueciSenhaModal from "../../components/EsqueciSenhaModal";
import { EMPENDENCIA } from "../../config";
import { formatUsername } from "../../utils/formatters";
import {
  deveMarcarAvisoEmpendencia,
  marcarAvisoEmpendenciaPendente,
} from "../../utils/empendencia";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";

function Login() {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [openEsqueciSenha, setOpenEsqueciSenha] = useState(false);
  const [requiresRecaptcha, setRequiresRecaptcha] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [infoMessage, setInfoMessage] = useState(location.state?.message || "");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "username" ? formatUsername(value) : value,
    }));
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let recaptchaToken = null;

      if (requiresRecaptcha) {
        if (!executeRecaptcha) {
          setError("reCAPTCHA não está pronto. Aguarde um momento.");
          setLoading(false);
          return;
        }

        recaptchaToken = await executeRecaptcha("login");

        if (!recaptchaToken) {
          setError("Falha ao obter token reCAPTCHA. Tente novamente.");
          setLoading(false);
          return;
        }
      }

      const userData = await authService.login(
        formData.username,
        formData.password,
        recaptchaToken
      );

      if (deveMarcarAvisoEmpendencia(EMPENDENCIA, userData)) {
        marcarAvisoEmpendenciaPendente();
      }

      if (userData && userData.data.isAnalista) {
        window.location.href = "/dashboard-analista";
      } else if (userData && userData.data.isSecretario) {
        window.location.href = "/secretario/dashboard";
      } else {
        window.location.href = "/dashboard";
      }
    } catch (err) {
      console.error("Erro durante login:", err);
      if (err.requiresRecaptcha) {
        setRequiresRecaptcha(true);
      }
      setError(getApiErrorMessage(err, "Erro ao fazer login"));
      setLoading(false);
    }
  }, [executeRecaptcha, requiresRecaptcha, formData.username, formData.password]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        background: `linear-gradient(45deg, ${alpha(
          theme.palette.primary.main,
          0.05
        )} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
        py: { xs: 4, sm: 6, md: 8 },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Container maxWidth="sm" sx={{ px: { xs: 0, sm: 2 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
            maxWidth: { xs: "100%", sm: 480 },
            mx: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <LoginIcon
              sx={{
                fontSize: 48,
                color: theme.palette.primary.main,
                mb: 2,
              }}
            />
            <Typography
              component="h1"
              variant="h4"
              sx={{
                mb: 1,
                fontWeight: 700,
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" },
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Bem-vindo
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
              Entre com suas credenciais para continuar
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
                {error}
              </Alert>
            )}

            {infoMessage && (
              <Alert
                severity="success"
                sx={{ width: "100%", mb: 2 }}
                onClose={() => setInfoMessage("")}
              >
                {infoMessage}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ width: "100%" }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Nome de usuário"
                name="username"
                autoComplete="username"
                autoFocus
                value={formData.username}
                onChange={handleChange}
                inputProps={{ maxLength: 20 }}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Senha"
                type={showPassword ? "text" : "password"}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  textTransform: "none",
                  borderRadius: 2,
                }}
              >
                {loading ? "Entrando..." : "Entrar"}
              </Button>
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenEsqueciSenha(true);
                  }}
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Esqueci minha senha
                </Link>
              </Box>
            </Box>

            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Typography variant="body2" color="textSecondary">
                Não tem uma conta?{" "}
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate("/registro")}
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Registre-se
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
      <EsqueciSenhaModal
        open={openEsqueciSenha}
        onClose={() => setOpenEsqueciSenha(false)}
      />
    </Box>
  );
}

export default Login;
